import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Unlock } from "lucide-react";

/**
 * Displays one collaborator's CRA entries for a given month, scoped to the
 * current mission. Extracted from `MissionDetail.jsx` during the Iter-78
 * refactor to keep the parent file under control.
 *
 * Props:
 *   - open: boolean
 *   - viewCra: { user_id, user_name, year, month } | null
 *   - craData: { submission, entries } | null
 *   - missionId: id of the mission whose entries we want to display
 *   - canEdit: bool — gates the "Unlock this month" button
 *   - onClose: () => void
 *   - onChangeMonth: (year, month) => void
 *   - onUnlock: (user_id, year, month) => void
 */
export default function ViewCraDialog({ open, viewCra, craData, missionId, canEdit, onClose, onChangeMonth, onUnlock }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-auto" data-testid="view-cra-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>CRA — {viewCra?.user_name}</span>
            {craData?.submission?.status && (
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">{craData.submission.status}</span>
            )}
          </DialogTitle>
          <DialogDescription>Live consumption for this collaborator — you can navigate months and unlock if needed.</DialogDescription>
        </DialogHeader>
        {viewCra && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const d = new Date(viewCra.year, viewCra.month - 2, 1);
                  onChangeMonth(d.getFullYear(), d.getMonth() + 1);
                }}
                data-testid="view-cra-prev"
                className="px-3 h-9 border border-zinc-200 rounded-md text-sm hover:bg-zinc-50"
              >‹ Prev</button>
              <div className="font-heading font-semibold text-lg">
                {new Date(viewCra.year, viewCra.month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </div>
              <button
                onClick={() => {
                  const d = new Date(viewCra.year, viewCra.month, 1);
                  onChangeMonth(d.getFullYear(), d.getMonth() + 1);
                }}
                data-testid="view-cra-next"
                className="px-3 h-9 border border-zinc-200 rounded-md text-sm hover:bg-zinc-50"
              >Next ›</button>

              {canEdit && craData?.submission?.status && craData.submission.status !== "DRAFT" && craData.submission.status !== "UNLOCK_APPROVED" && (
                <button
                  onClick={() => onUnlock(viewCra.user_id, viewCra.year, viewCra.month)}
                  data-testid="unlock-cra-btn"
                  className="ml-auto inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
                >
                  <Unlock size={13} /> Unlock this month
                </button>
              )}
            </div>

            {/* Entries for this mission only */}
            <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-80 overflow-y-auto">
              {(craData?.entries || []).filter((e) => e.mission_id === missionId).length === 0 && (
                <div className="px-4 py-6 text-sm text-zinc-500 text-center">No hours booked on this mission.</div>
              )}
              {(craData?.entries || [])
                .filter((e) => e.mission_id === missionId)
                .sort((a, b) => a.date.localeCompare(b.date))
                .map((e) => (
                  <div key={e.entry_id} className="px-4 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-zinc-600">{e.date}</div>
                      <div className="font-mono">{e.hours}h</div>
                    </div>
                    {e.on_call_reason && (
                      <div className="mt-1 text-[11px] px-2 py-1 bg-amber-50 border border-amber-200 rounded" data-testid={`on-call-motif-${e.entry_id}`}>
                        <span className="font-mono uppercase text-[9px] text-amber-900 mr-2">
                          {e.on_call_holiday ? `HOLIDAY · ${e.on_call_holiday}` : "WEEKEND"}
                        </span>
                        <span className="text-amber-900">{e.on_call_reason}</span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
            <div className="text-xs text-zinc-500 font-mono">
              Total this month on this mission: {((craData?.entries || []).filter((e) => e.mission_id === missionId).reduce((s, e) => s + e.hours, 0) / 8).toFixed(2)} JH
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
